import { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Progress } from './ui/progress';
import { Textarea } from './ui/textarea';
import { TrendingUp, TrendingDown, Activity, Heart, Target, Calendar, Download, FileText, Award, AlertCircle, ArrowLeft } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { PatientLayout } from './patient/PatientLayout';
import { useAuth } from '../lib/hooks/useAuth';
import { useMyDashboard } from '../lib/hooks/useDashboards';
import { useMyVitals } from '../lib/hooks/useVitals';
import { useMyCombinedTrends } from '../lib/hooks/useTrends';
import { LoadingState } from './common/LoadingState';
import { AlertCard } from './common/AlertCard';
import { EmptyState } from './common/EmptyState';

interface ReportsAnalyticsProps {
  onBack?: () => void;
  onNavigate?: (view: string) => void;
  onLogout?: () => void;
}

export function ReportsAnalytics({ onBack, onNavigate, onLogout }: ReportsAnalyticsProps) {
  const { user } = useAuth();

  // ✅ FIX: Memoize dates to prevent continuous re-fetching
  const dateRange = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000); // Last 30 days
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
  }, []); // Empty deps = only calculate once on mount

  // ✅ FIX: Use /me endpoints instead of patient_id based endpoints
  const { data: dashboardData, isLoading: dashboardLoading } = useMyDashboard();
  const { data: vitalsArray, isLoading: vitalsLoading } = useMyVitals(
    dateRange.startDate, // ✅ Use stable date
    dateRange.endDate    // ✅ Use stable date
  );
  const { data: trendsData, isLoading: trendsLoading } = useMyCombinedTrends(12); // 12 weeks

  // Loading state
  if (dashboardLoading || vitalsLoading || trendsLoading) {
    return (
      <PatientLayout currentView="reports" onNavigate={onNavigate} onLogout={onLogout}>
        <LoadingState message="Loading your progress report..." />
      </PatientLayout>
    );
  }

  // Calculate averages from actual data
  const vitals = vitalsArray || [];
  const avgSpo2 = vitals.length > 0 
    ? Math.round(vitals.reduce((sum, v) => sum + (v.spo2 || 0), 0) / vitals.length)
    : 0;
  const avgHR = vitals.length > 0
    ? Math.round(vitals.reduce((sum, v) => sum + (v.heart_rate || 0), 0) / vitals.length)
    : 0;
  const currentAdherence = dashboardData?.overall_adherence || 0;

  // Map vitals data for charts (from API)
  const vitalsChartData = vitals.slice(0, 7).map(v => ({
    day: new Date(v.recorded_at).toLocaleDateString('en-US', { weekday: 'short' }),
    spo2: v.spo2 || 0,
    heartRate: v.heart_rate || 0,
  }));

  // Map adherence trend from trends data (from API)
  const adherenceTrendData = trendsData?.data.slice(0, 8).map((item, idx) => ({
    week: `W${idx + 1}`,
    adherence: Math.round((item.fev1 || 0) / 100 * 85), // Approximate adherence from FEV1
  })) || [];

  return (
    <div className="min-h-screen bg-[#D4F0F8] pb-20">
      {/* Header */}
      <div className="bg-primary p-6">
        <div className="flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="text-primary-foreground">
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}
          <div>
            <h1 className="text-primary-foreground">Progress Report</h1>
            <p className="text-primary-foreground/80">Your rehabilitation journey</p>
          </div>
        </div>
      </div>

      <div className="p-4">
        <Tabs defaultValue="summary" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="space-y-4">
            {/* Overall Progress Circle */}
            <Card className="shadow-lg">
              <CardContent className="p-8">
                <div className="flex flex-col items-center">
                  <div className="relative w-40 h-40">
                    {/* Circular Progress */}
                    <svg className="w-40 h-40 transform -rotate-90">
                      <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="#E5E5E3"
                        strokeWidth="12"
                        fill="none"
                      />
                      <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="#14CAF4"
                        strokeWidth="12"
                        fill="none"
                        strokeDasharray={`${(currentAdherence / 100) * 440} 440`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-[#14CAF4]" style={{ fontSize: '32px', fontWeight: 'bold' }}>
                        {currentAdherence}%
                      </span>
                      <span className="text-muted-foreground">Overall</span>
                    </div>
                  </div>
                  <h3 className="text-primary mt-4">Excellent Progress!</h3>
                  <p className="text-muted-foreground text-center mt-2">
                    You're doing great with your rehabilitation program
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Key Metrics */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary">Key Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-[#14CAF4]/10 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-5 h-5 text-[#14CAF4]" />
                      <p className="text-muted-foreground">Avg SpO₂</p>
                    </div>
                    <p className="text-[#14CAF4]">{avgSpo2}%</p>
                    <p className="text-muted-foreground">Normal</p>
                  </div>

                  <div className="p-4 bg-accent/10 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className="w-5 h-5 text-accent" />
                      <p className="text-muted-foreground">Avg Heart Rate</p>
                    </div>
                    <p className="text-accent">{avgHR} bpm</p>
                    <p className="text-muted-foreground">Normal</p>
                  </div>

                  <div className="p-4 bg-primary/10 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      <p className="text-muted-foreground">Sessions</p>
                    </div>
                    <p className="text-primary">42</p>
                    <p className="text-muted-foreground">Completed</p>
                  </div>

                  <div className="p-4 bg-[#FEEBDA] rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-5 h-5 text-accent" />
                      <p className="text-muted-foreground">Current Streak</p>
                    </div>
                    <p className="text-accent">12 days</p>
                    <p className="text-muted-foreground">Keep going!</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Adherence Breakdown */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary">This Week's Adherence</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="flex justify-between mb-2">
                    <span>Exercises</span>
                    <span className="text-primary">90%</span>
                  </div>
                  <Progress value={90} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span>Vitals Logging</span>
                    <span className="text-[#14CAF4]">100%</span>
                  </div>
                  <Progress value={100} className="h-2 [&>div]:bg-[#14CAF4]" />
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span>Feedback Response</span>
                    <span className="text-accent">85%</span>
                  </div>
                  <Progress value={85} className="h-2 [&>div]:bg-accent" />
                </div>
              </CardContent>
            </Card>

            {/* Download Button */}
            <Button 
              className="w-full bg-accent hover:bg-accent/90 h-12 rounded-xl"
              onClick={() => toast.success('Generating PDF report...', {
                description: 'Your progress report will download shortly.'
              })}
            >
              <Download className="w-5 h-5 mr-2" />
              Download Report (PDF)
            </Button>
          </TabsContent>

          <TabsContent value="trends" className="space-y-4">
            {/* Adherence Trend Chart */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary">6-Week Adherence Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={adherenceTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                      <XAxis dataKey="week" stroke="#494949" />
                      <YAxis domain={[0, 100]} stroke="#494949" />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="adherence"
                        stroke="#2762AC"
                        strokeWidth={3}
                        dot={{ fill: '#2762AC', r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Vitals Trend */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary">SpO₂ & Heart Rate Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={vitalsChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                      <XAxis dataKey="day" stroke="#494949" />
                      <YAxis stroke="#494949" />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="spo2"
                        stroke="#14CAF4"
                        strokeWidth={2}
                        name="SpO₂"
                      />
                      <Line
                        type="monotone"
                        dataKey="heartRate"
                        stroke="#F07D13"
                        strokeWidth={2}
                        name="Heart Rate"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Insights */}
            <Card className="shadow-lg bg-[#FEEBDA]">
              <CardContent className="p-6">
                <h3 className="text-primary mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Insights & Achievements
                </h3>
                <ul className="space-y-2 text-foreground">
                  <li className="flex gap-2">
                    <span className="text-accent">🎯</span>
                    <span>15% improvement in adherence over 6 weeks</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-accent">⭐</span>
                    <span>Maintained SpO₂ above 95% consistently</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-accent">🔥</span>
                    <span>12-day exercise streak - personal best!</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-accent">💪</span>
                    <span>Completed all breathing exercises this week</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notes" className="space-y-4">
            {/* Doctor's Notes */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Doctor's Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-secondary rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span>Dr. Meera Krishnan</span>
                    <span className="text-muted-foreground">Oct 10, 2025</span>
                  </div>
                  <p className="text-foreground">
                    Patient showing excellent progress. SpO₂ levels stable and improving. 
                    Continue current exercise regimen. Consider increasing intensity next week.
                  </p>
                </div>

                <div className="p-4 bg-secondary rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span>Dr. Meera Krishnan</span>
                    <span className="text-muted-foreground">Oct 3, 2025</span>
                  </div>
                  <p className="text-foreground">
                    Good adherence to breathing exercises. Vitals are within normal range. 
                    Recommend maintaining current schedule.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Therapist Feedback */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Therapist Feedback
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-[#14CAF4]/10 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span>Suresh Nair</span>
                    <span className="text-muted-foreground">Oct 11, 2025</span>
                  </div>
                  <p className="text-foreground">
                    Great job on your breathing technique! Your diaphragmatic breathing 
                    has improved significantly. Keep up the excellent work!
                  </p>
                </div>

                <div className="p-4 bg-[#14CAF4]/10 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span>Suresh Nair</span>
                    <span className="text-muted-foreground">Oct 8, 2025</span>
                  </div>
                  <p className="text-foreground">
                    Focus on slower, deeper breaths during pursed lip breathing exercises. 
                    You're making great progress overall.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Patient Notes */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary">My Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Add your own notes about how you're feeling, any challenges, or improvements you've noticed..."
                  className="min-h-32 rounded-xl"
                />
                <Button 
                  className="w-full mt-4 bg-accent hover:bg-accent/90 rounded-xl"
                  onClick={() => toast.success('Note saved successfully!', {
                    description: 'Your personal note has been added to your health record.'
                  })}
                >
                  Save Note
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}